import { CommonHelperFunctions } from "@kuika/kuika-cl-model";
import { StreamingHelperService } from "./streaming-helper-service";

const backendUrl: string = CommonHelperFunctions.GetBackendUrl();

/** The request itself failed — the server never took the generation on. */
export const GENERATION_REQUEST_FAILED_MESSAGE: string = "An error occurred. Please reach HelpDesk.";

/** The stream ended without the server's verdict: a proxy timeout, a reset, a restart. */
export const GENERATION_STREAM_LOST_MESSAGE: string =
  "The connection was lost before code generation finished. Please try again.";

/**
 * Every generation ends on exactly one of these. ExecutePreview, not CodeGenerated, closes the
 * success path: the server writes it last, and it carries the result (validations, URLs).
 */
const TERMINAL_EVENTS: ReadonlySet<string> = new Set<string>(["ExecutePreview", "ErrorOccured", "GenerationCancelled"]);

type SseListener = (data: string) => void;

interface SseListenerMap {
  [eventName: string]: SseListener;
}

interface ISseEvent {
  eventName: string;
  data: string;
}

export class CodeGenSseService {
  private abortController: AbortController | null = null;

  private generationId: string | null = null;

  private listeners: SseListenerMap = {};

  private codeGeneratedCallback: ((message: string) => void) | null = null;

  private errorOccuredCallback: ((message: string) => void) | null = null;

  private generationCancelledCallback: (() => void) | null = null;

  public async stopConnection(): Promise<void> {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.generationId = null;
  }

  public async invokeFunction(jsonBody: string): Promise<void> {
    const url: string = `${backendUrl}/codegen/generate`;
    const controller: AbortController = new AbortController();
    this.abortController = controller;

    let response: Response;
    try {
      response = await StreamingHelperService.authenticatedFetch(url, {
        method: "POST",
        body: jsonBody,
        signal: controller.signal
      });
    } catch {
      this.failGeneration(controller, GENERATION_REQUEST_FAILED_MESSAGE);
      return;
    }

    if (!response.ok) {
      this.failGeneration(controller, GENERATION_REQUEST_FAILED_MESSAGE);
      return;
    }

    this.generationId = response.headers.get("X-Generation-Id");

    // Read SSE stream in background — invokeFunction resolves after headers arrive
    // (matches SignalR invoke() which resolved after server acknowledged). The reader settles
    // the generation itself; all that can still escape is a subscriber throwing.
    this.readSseStream(response, controller).catch((error: unknown): void => {
      console.error(error);
    });
  }

  public async cancelGeneration(): Promise<void> {
    if (this.generationId) {
      try {
        await StreamingHelperService.authenticatedFetch(`${backendUrl}/codegen/${this.generationId}/cancel`, {
          method: "POST"
        });
      } catch {
        // Cancel may fail if generation already completed
      }
    }
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  public onMessage(eventName: string, callback: SseListener): void {
    this.listeners[eventName] = callback;
  }

  public removeAllListeners(): void {
    this.listeners = {};
  }

  /**
   * A generation (preview/publish/download) is in flight or its stream is still open.
   * Used to keep the standalone Issues validation off the wire while codegen runs —
   * the generation itself delivers fresher validations into the same state.
   */
  public isGenerationActive(): boolean {
    return this.abortController !== null;
  }

  public setCodeGeneratedCallback(callback: ((message: string) => void) | null): void {
    this.codeGeneratedCallback = callback;
  }

  public setErrorOccuredCallback(callback: ((message: string) => void) | null): void {
    this.errorOccuredCallback = callback;
  }

  public setGenerationCancelledCallback(callback: (() => void) | null): void {
    this.generationCancelledCallback = callback;
  }

  private async readSseStream(response: Response, controller: AbortController): Promise<void> {
    let hasTerminalEvent: boolean = false;
    const dispatch: (block: string) => void = (block: string): void => {
      const event: ISseEvent | null = this.parseSseEvent(block);
      if (!event) return;
      // Marked before emitting, so a subscriber that throws cannot make a delivered verdict look lost.
      if (TERMINAL_EVENTS.has(event.eventName)) hasTerminalEvent = true;
      this.emitEvent(event.eventName, event.data);
    };

    try {
      const reader: ReadableStreamDefaultReader<Uint8Array> | undefined = response.body?.getReader();
      if (!reader) return;

      const decoder: TextDecoder = new TextDecoder();
      let buffer: string = "";

      while (true) {
        let chunk: ReadableStreamReadResult<Uint8Array>;
        try {
          chunk = await reader.read();
        } catch {
          // A proxy timing the connection out typically resets it mid-body, so the read rejects
          // instead of reporting done. Our own abort lands here too; failGeneration tells them apart.
          break;
        }
        if (chunk.done) break;

        buffer += decoder.decode(chunk.value, { stream: true });
        const blocks: string[] = buffer.split("\n\n");
        buffer = blocks.pop() ?? "";

        for (const block of blocks) {
          dispatch(block);
        }
      }

      if (buffer.trim()) {
        dispatch(buffer);
      }
    } finally {
      if (!hasTerminalEvent) this.failGeneration(controller, GENERATION_STREAM_LOST_MESSAGE);
    }
  }

  /**
   * Ends a generation the server will never deliver a verdict for: the request failed, or the
   * stream ended before a terminal event. Replays the server's own failure sequence
   * (SetSpinnerOff → ErrorOccured), so every subscriber unwinds exactly as it does when the server
   * reports an error: spinner off, Run button reset, Issues validation released.
   */
  private failGeneration(controller: AbortController, message: string): void {
    // Our own abort (cancelGeneration / stopConnection clear the field), or a newer generation
    // has taken the service over — either way not this stream's failure to report.
    if (this.abortController !== controller) return;

    this.abortController = null;
    this.generationId = null;
    this.emitEvent("SetSpinnerOff", "");
    this.emitEvent("ErrorOccured", message);
  }

  /** One SSE block → its event; null for blocks without an `event:` field, such as the keep-alive comment. */
  private parseSseEvent(block: string): ISseEvent | null {
    let eventName: string = "";
    let data: string = "";

    for (const line of block.split("\n")) {
      if (line.startsWith("event: ")) {
        eventName = line.slice(7).trim();
      } else if (line.startsWith("data: ")) {
        // SSE spec: bir olayın birden çok "data:" satırı \n ile birleştirilir.
        data += (data ? "\n" : "") + line.slice(6);
      }
    }

    return eventName ? { eventName, data } : null;
  }

  private emitEvent(eventName: string, data: string): void {
    const listener: SseListener | undefined = this.listeners[eventName];
    if (listener) {
      listener(data);
    }

    if (eventName === "CodeGenerated" && this.codeGeneratedCallback) {
      this.codeGeneratedCallback(data);
    } else if (eventName === "ErrorOccured" && this.errorOccuredCallback) {
      this.errorOccuredCallback(data);
    } else if (eventName === "GenerationCancelled" && this.generationCancelledCallback) {
      this.generationCancelledCallback();
    }
  }
}

const codeGenSseService: CodeGenSseService = new CodeGenSseService();
export default codeGenSseService;
